import {
  createSpace,
  getCreatedSpace,
  createSpaceStep,
  clearAppliedTemplates,
  uploadProgress,
} from "app/actions/spaceCreation";
import { fetchAcademySetup } from "app/actions/academySetup";
import {
  isSetupItemOpen,
  showInvitationSentToast,
} from "app/components/AcademySetup/SetupToast";
import { isAcademyOnboardingCoach } from "app/utils/subscription";
import { useActions } from "app/utils/hooks";
import { useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { RootState } from "typedefs";

export const useCreateNewSpaceModel = () => {
  const history = useHistory();
  const location = useLocation();
  const [pending, setPending] = useState(false);

  const route = (path: string) => {
    history.push(path);
  };

  const createSpaceActions = useActions({
    createSpace,
    getCreatedSpace,
    clearAppliedTemplates,
    fetchAcademySetup,
  });
  const dispatch = useDispatch();
  const { step, spaces_import, error, data } = useSelector(
    (state: RootState) => state.createSpace,
    shallowEqual,
  );
  const self = useSelector((state: RootState) => state.user.self, shallowEqual);
  const stepForward = () => {
    if (step !== 3) {
      dispatch(createSpaceStep(step + 1));
    }
  };

  const stepBackward = () => {
    if (step !== 1) {
      dispatch(createSpaceStep(step - 1));
    }
  };

  const cancelHandler = () => {
    createSpaceActions.clearAppliedTemplates();
    route("/spaces");
  };

  const onCreateSpace = () => {
    const extractUserProps = (users: any) => {
      if (!users || !Array.isArray(users)) {
        return [];
      }

      return users.map((user: any) => ({
        email: user.email ?? null,
        name: user.name ?? user.displayName ?? null,
        phone: user.phone ?? "",
        phone_country: user.phoneCountry || "",
        user_id: user.id ?? null,
      }));
    };

    const newBaseSpaceApplication = {
      athletes: extractUserProps(spaces_import.athletes),
      coaches: extractUserProps(spaces_import.coaches),
      contributors: extractUserProps(spaces_import.contributors),
      create_for_self: spaces_import.create_for_self,
      segment_ids:
        spaces_import.segment_ids !== undefined
          ? spaces_import.segment_ids
          : [],
      space_template_id: spaces_import?.space_template_id.id,
      sport: spaces_import?.sport !== undefined ? spaces_import?.sport : "",
      sport_other:
        spaces_import?.sport_other !== undefined
          ? spaces_import?.sport_other
          : "",
    };

    // Both flows complete the checklist's invite_athlete item. Fired as soon
    // as the create resolves and deliberately NOT tied to the import's
    // progress: the alert polls /academy/setup itself and shows only once the
    // BE confirms the item, so it survives whatever shape the import status
    // comes back in. Never throws — the thunk resolves fetch errors into the
    // error slice.
    //
    // Read before the create, or every later space re-celebrates the first.
    // What counts as an invite is the BE's call, not this form's: it flips
    // invite_athlete for a space the coach created for themself too (the
    // form's default), so no local rule may veto a completion the API then
    // confirms (product decision 2026-09-18).
    const completesAcademySetupItem =
      isAcademyOnboardingCoach(self) &&
      isSetupItemOpen("invite_athlete");
    // Fire-and-forget from both branches below; the alert is best-effort
    // feedback, so a failure inside it is logged rather than surfaced or
    // left as an unhandled rejection.
    const notifyInvitationSent = async () => {
      if (!completesAcademySetupItem) {
        return;
      }
      try {
        await showInvitationSentToast(
          createSpaceActions.fetchAcademySetup,
          (path, state) => history.push(path, state),
        );
      } catch (error) {
        console.error("Error confirming the invite_athlete setup item:", error);
      }
    };

    const isMultiple = location?.pathname?.includes("new/multiple");

    setPending(true);
    if (isMultiple) {
      createSpaceActions
        .createSpace({
          spaces_import: newBaseSpaceApplication,
        } as any)
        .then((res) => {
          notifyInvitationSent();

          const refreshProgress = async () => {
            try {
              if (res && (res?.spaceImportId || res?.spaceId)) {
                if (res?.spaceId) {
                  route(`/spaces/${res.spaceId}`);
                }
                const spaceData = await createSpaceActions.getCreatedSpace(
                  res?.spaceImportId || res?.spaceId,
                );
                const progress = spaceData?.status?.progress;
                if (progress) {
                  dispatch(uploadProgress(progress));
                }
                if (parseInt(progress, 10) === 100) {
                  createSpaceActions.clearAppliedTemplates();
                  route("/spaces");
                } else if (spaceData?.status) {
                  setTimeout(refreshProgress, 1000);
                }
              }
            } catch (error) {
              console.error("Error polling space status:", error);
            }
          };

          refreshProgress();
        })
        .catch((err) => {
          // The success path keeps pending while the import is polled and
          // then routes away; a failed POST has nothing to poll, so release
          // the form the way the single-space branch does.
          console.error("Error creating spaces:", err);
          createSpaceActions.clearAppliedTemplates();
          setPending(false);
        });
    } else {
      createSpaceActions
        .createSpace({
          spaces_import: newBaseSpaceApplication,
        } as any)
        .then((res) => {
          // createSpaceActions.clearAppliedTemplates();
          route(`/spaces/${res.spaceId}`);
          // Not awaited: pending should clear as soon as the space exists,
          // not after the checklist round-trip behind the alert.
          notifyInvitationSent();
        })
        .catch((err) => {
          console.error("Error creating space:", err);
        })
        .finally(() => {
          createSpaceActions.clearAppliedTemplates();
          setPending(false);
        });
    }
  };

  return {
    stepForward,
    stepBackward,
    onCreateSpace,
    cancelHandler,
    data,
    pending,
    error,
  };
};
